import { Capacitor } from "@capacitor/core";
import { useToast as useChakraToast } from "@chakra-ui/core";
import { GqlOpenBankingStatus } from "@penfold/graphql";
import { ApolloError } from "apollo-client";
import Toast from "atoms/Toast";
import gql from "graphql-tag";
import { debounce } from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useApolloClient, useLazyQuery, useQuery } from "react-apollo";
import { useSet } from "react-use";
import { TerminalOpenBankingStatuses } from "./OpenBankingStatus/types";

const CHECK_IDENTITY_RESULT = gql`
  query checkIdentityResult {
    checkIdentityDocumentResult {
      id
      milestone
      features
      amlDocumentResult {
        id
        pass
        status
      }
    }
  }
`;

const POLL_OPEN_BANKING_PAYMENT = gql`
  query openBankingPayment($paymentId: String!) {
    openBankingPayment(paymentId: $paymentId) {
      status
      isOnboarding
    }
  }
`;

export const useVerificationPolling = (user) => {
  const [startChecking] = useLazyQuery(CHECK_IDENTITY_RESULT, {
    pollInterval: 5000,
  });

  const result = user?.amlDocumentResult;
  const shouldPoll = result && result.status === "Waiting";

  useEffect(() => {
    if (shouldPoll) {
      startChecking();
    }
  }, [shouldPoll]);
};

export type PollingStatus =
  | TerminalOpenBankingStatuses
  | "Pending"
  | "Processing";

export type PaymentStatusPollingResponse = Readonly<{
  status: PollingStatus;
  isOnboarding: boolean;
  error: ApolloError;
  loading: boolean;
}>;

export const usePaymentStatusPolling = (
  paymentId
): PaymentStatusPollingResponse => {
  if (!paymentId) {
    return {
      status: "Pending",
      isOnboarding: false,
      loading: true,
      error: null,
    };
  }
  const [pollStatus, { data, error }] = useLazyQuery(
    POLL_OPEN_BANKING_PAYMENT,
    { variables: { paymentId }, fetchPolicy: "no-cache" }
  );

  const paymentStatus = data?.openBankingPayment.status;
  const isOnboarding = data?.openBankingPayment.isOnboarding;
  const terminalStatuses: GqlOpenBankingStatus[] = [
    "Failed",
    "Completed",
    "Rejected",
  ];
  const isTerminalStatus = terminalStatuses.includes(paymentStatus);
  const isLoading = !isTerminalStatus && !error;
  const [status, setStatus] = useState({
    isOnboarding,
    status: paymentStatus,
    loading: isLoading,
    error,
  });

  useEffect(() => {
    // `stopPolling` option with Apollo `useQuery` does not stop the polling
    // so using setInterval and useLazyQuery
    const poll = setInterval(() => {
      pollStatus();
    }, 2000);

    const timeout = setTimeout(() => {
      setStatus({
        isOnboarding,
        status: "Processing",
        loading: false,
        error: null,
      });
    }, 120000); // 2 minute timeout

    setStatus({
      isOnboarding,
      status: paymentStatus,
      loading: isLoading,
      error,
    });

    const clear = () => {
      clearTimeout(timeout);
      clearInterval(poll);
    };

    if (isTerminalStatus) {
      clear();
    }

    return clear;
  }, [paymentStatus, isLoading, isTerminalStatus, error]);
  return status;
};

export const useAppStateRefresh = () => {
  const client = useApolloClient();

  useEffect(() => {
    if (Capacitor.isPluginAvailable("App")) {
      const AppPlugin = Capacitor.Plugins.App;
      const listener = AppPlugin.addListener("appStateChange", (state) => {
        if (state.isActive) {
          console.info(
            "App was made active. Refetching observable apollo queries"
          );
          void client.reFetchObservableQueries();
        }
      });

      return listener.remove;
    }
  }, [client]);
};
export interface FilterMethodsType {
  add: (key: string) => void;
  has: (key: string) => boolean;
  remove: (key: string) => void;
  toggle: (key: string) => void;
  reset: () => void;
  clear: () => void[];
}

export type UseFiltersResult = [Array<string>, FilterMethodsType];

export const useFilters = (defaults: Array<string> = []): UseFiltersResult => {
  const [set, { add, has, remove, reset, toggle }] = useSet(new Set(defaults));

  const filterMethods = {
    add,
    has,
    remove,
    toggle,
    reset,
    clear: () => Array.from(set).map((entry) => remove(entry)),
  };

  return [Array.from(set), filterMethods];
};

export const useToast = () => {
  const toast = useChakraToast();

  const notify = useCallback(
    (args) => {
      toast({
        render: (props) => <Toast {...args} {...props} />,
        ...args,
      });
    },
    [toast]
  );
  return notify;
};

export const useScrollToTop = () => {
  const scrollWindow = () => window.scroll({ top: 0 });
  // Fire as soon as possible, to prevent visual glitches
  useLayoutEffect(() => {
    setTimeout(scrollWindow);
  }, []);

  // Fire again after useEffect, to ensure a successful scroll in case of a race condition with other components using useEffect
  useEffect(scrollWindow);
};

export const useIntercomLauncherDisplay = (visible) => {
  useEffect(() => {
    if (Capacitor.isPluginAvailable("IntercomPlugin")) {
      const capacitorDisplayFn = visible
        ? Capacitor.Plugins.IntercomPlugin.displayLauncher
        : Capacitor.Plugins.IntercomPlugin.hideLauncher;
      capacitorDisplayFn();
    } else {
      // We are explicitly setting the intercomSettings on the window for every
      // trigger of this effect, rather than passing visibility as an option as
      // per docs, because Segment also calls update on every page. Calling
      // update sets the launcher back to the values in window.intercomSettings.
      // https://www.intercom.com/help/en/articles/189-turn-off-show-or-hide-the-intercom-messenger
      window.intercomSettings = { hide_default_launcher: !visible };
      window.Intercom?.("update");
    }
  }, [visible]);
};

export type SmallScreenOptions = {
  minWidth: number;
  minHeight: number;
};

// custom hook for detecting small screens based on window size
export function useSmallScreen(
  options: SmallScreenOptions = {
    minWidth: 640, // same as "small" breakpoint defined in frontend/lib/theme.ts
    minHeight: 640,
  }
): boolean {
  const check = () =>
    window.innerWidth < options.minWidth ||
    window.innerHeight < options.minHeight;

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(check());

  // helper function so that we can add/remove listeners
  const updateState = () => setIsSmallScreen(check());

  useEffect(() => {
    window.addEventListener("resize", updateState);
    return () => window.removeEventListener("resize", updateState);
  });

  return isSmallScreen;
}

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

const GET_USER_ID_LAZY_QUERY = gql`
  query userIdLazy {
    me {
      id
    }
  }
`;

/**
 * Get the active user's ID lazily, ie. wait until another query makes
 * the actual network request.
 */
export function useUserIdLazyWithClient(client) {
  const { data } = useQuery(GET_USER_ID_LAZY_QUERY, {
    client,
    fetchPolicy: "cache-only",
  });

  return data?.me?.id;
}

export const useScrollElementToTop = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView();
    }
  }, [ref]);
  return ref;
};

export type PartialVisualViewport = Partial<VisualViewport>;

function getVisualViewportInnerHeight() {
  if (!process.browser || !("visualViewport" in window)) {
    return null;
  }
  return window.visualViewport.height - window.visualViewport.offsetTop;
}

export const useVisualViewport = ({
  enabled,
}: {
  enabled: boolean;
}): PartialVisualViewport => {
  const [viewport, setViewport] = useState<PartialVisualViewport>({
    height: enabled ? getVisualViewportInnerHeight() : null,
  });

  useEffect(() => {
    if (!enabled || !("visualViewport" in window)) {
      return;
    }

    // Do this once now to handle the case where the hook has been
    // enabled for the first time after its initial load.
    setViewport({
      height: getVisualViewportInnerHeight(),
    });

    const handler = debounce(() => {
      setViewport({
        height: getVisualViewportInnerHeight(),
      });
    }, 50);

    window.visualViewport.addEventListener("scroll", handler);
    window.visualViewport.addEventListener("resize", handler);

    return () => {
      window.visualViewport.removeEventListener("scroll", handler);
      window.visualViewport.removeEventListener("resize", handler);
    };
  }, [enabled]);

  return viewport;
};
